import type { QueryList } from '@angular/core';
import { ChangeDetectorRef, Component, inject, ViewChildren } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { trackByIndex } from '@core/common-legacy';
import type { RtTextInput } from '@core/user-interface-legacy';
import { InputComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'rt-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.less'],
  imports: [InputComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CodeInputComponent,
      multi: true,
    },
  ],
})
export class CodeInputComponent implements ControlValueAccessor {
  public readonly CODE_LENGTH = 6;

  @ViewChildren('charInput') public inputs: QueryList<RtTextInput>;

  public trackByIndex = trackByIndex;
  public values: string[] = new Array(this.CODE_LENGTH).fill('');

  public onChange: (value: string) => void;
  public onTouched: () => void;
  private cdRef = inject(ChangeDetectorRef);

  public writeValue(value: string) {
    const parsed = value.replace(/\D/g, '').slice(0, 6).split('');
    this.values = this.values.map((_, index) => parsed[index] || '');
  }

  public registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  public ngAfterViewInit() {
    this.values.forEach((char, index) => this.setChar(char, index));
  }

  public valueChange(value: string, index: number) {
    if (value.length === 1) {
      this.changeSingleChar(value, index);
    } else if (value.length > 1) {
      this.changeMultipleChars(value, index);
    }
    this.values = this.inputs.map(input => input.getNativeInputElement().value);
    this.onChange(this.values.join(''));
    this.cdRef.detectChanges();
  }

  public valueSelect(index: number) {
    this.inputs?.get(index)?.getNativeInputElement().select();
  }

  private changeSingleChar(value: string, index: number) {
    this.setChar(value, index);
    if (index < this.CODE_LENGTH - 1) {
      this.setFocus(index + 1);
    }
  }

  private changeMultipleChars(value: string, index: number) {
    value.split('').forEach((char, charIndex) => {
      this.setChar(char, index + charIndex);
    });
    if (index + value.length < this.CODE_LENGTH) {
      this.setFocus(index + value.length);
    }
  }

  private setChar(char: string, index: number) {
    if (this.inputs.get(index)) {
      this.inputs.get(index)!.getNativeInputElement().value = char;
    }
  }

  private setFocus(index: number) {
    this.inputs.get(index)?.getNativeInputElement().focus();
  }
}
